import React, { useCallback, useState } from "react";
import { Collapsible, EditUserButton } from "../../../components";
import { Avatar, Badge, Button } from "../../../components/ui";
import { getFileUrl } from "../../../core/config/api.js";
import { ContactWizard } from "../../../views/Users/index.jsx";
import { useRequestContext } from "../../hooks/useRequest";
import { AssignClientPicker } from "./AssignClientPicker";
import useUserUpdates from "../../../hooks/useUserUpdates";
import "./assign-client.scss";

// Person/user icon — matches the magnifying-glass icon shape used in
// SearchProduct so the section header style stays consistent across the flow.
const UserIcon = () => (
  <svg
    className="rc-lp__section-icon"
    width="14"
    height="14"
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden
  >
    <circle cx="8" cy="5.5" r="2.5" stroke="currentColor" strokeWidth="1.3" />
    <path d="M3.5 13c0-2.5 2-4 4.5-4s4.5 1.5 4.5 4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
  </svg>
);

const displayClientName = (client) => {
  if (!client) return "—";
  if (client.full_name) return client.full_name;
  if (client.first_name || client.last_name) {
    return `${client.first_name || ""} ${client.last_name || ""}`.trim();
  }
  return client.email || `User #${client.id}`;
};

export function AssignClient({ step, idx, onAdvance }) {
  const {
    getContext,
    assignClient,
    toggleStep,
    client: contextClient,
    refreshClient,
    request,
  } = useRequestContext();
  const { isEditing } = getContext(idx);
  const [searchQuery, setSearchQuery] = useState("");
  const [isCreatingClient, setIsCreatingClient] = useState(false);
  const [isChangingClient, setIsChangingClient] = useState(false);

  const assignedClient = contextClient || request?.client;
  const showPicker = !assignedClient?.id || isChangingClient;

  // The assigned client is a snapshot taken when the row was picked, so a name /
  // type / store / email / phone edited in the edit tab has to be pushed back
  // in explicitly. Deliberately only the client — the request's
  // relation-preserving merge stays exactly as it is.
  const onClientRefreshed = useCallback(
    (fresh) => refreshClient?.(fresh),
    [refreshClient]
  );
  useUserUpdates(assignedClient?.id, onClientRefreshed, { refreshOnFocus: true });

  const onAssignClient = async (client) => {
    await assignClient(step, idx, client);
    setIsChangingClient(false);
    setIsCreatingClient(false);
    onAdvance?.();
  };

  const trigger = (
    <span className="search-product-trigger-label">
      <UserIcon />
      <span>{step.label}</span>
    </span>
  );

  return (
    <Collapsible
      trigger={trigger}
      open={step.open}
      // className={isEditing && !user.is_sales_admin ? "locked" : ""}
      // triggerDisabled={!user.is_sales_admin && isEditing}
      testId="assign-client"
      handleTriggerClick={() => toggleStep(idx)}
    >
      {isCreatingClient ? (
        <ContactWizard
          className="find-product-create-client"
          submitLabel="Select"
          afterSubmit={(client) => onAssignClient(client)}
          onCancel={() => setIsCreatingClient(false)}
        />
      ) : (
        <div className="mt-4 find-product-contacts assign-client">
          {assignedClient?.id && !isChangingClient && (
            <div className="assign-client__assigned">
              <div className="assign-client__assigned-card">
                <div className="assign-client__assigned-main">
                  <Avatar
                    name={displayClientName(assignedClient)}
                    src={
                      assignedClient.avatar_file_id
                        ? getFileUrl(assignedClient.avatar_file_id)
                        : undefined
                    }
                  />
                  <div className="assign-client__assigned-info">
                    <Badge tone="accent" size="sm" className="assign-client__assigned-badge">
                      Assigned
                    </Badge>
                    <div className="assign-client__assigned-name">
                      {displayClientName(assignedClient)}
                    </div>
                    <div className="assign-client__assigned-contact">
                      {assignedClient.email || assignedClient.phone || "—"}
                    </div>
                    {assignedClient.id && (
                      <div className="assign-client__assigned-id">#{assignedClient.id}</div>
                    )}
                  </div>
                </div>
                <div className="assign-client__assigned-actions">
                  <EditUserButton
                    user={assignedClient}
                    className="assign-client__edit-btn"
                    label="Edit client"
                  />
                  <Button
                    variant="secondary"
                    size="sm"
                    className="assign-client__change-btn"
                    onClick={() => setIsChangingClient(true)}
                  >
                    Change client
                  </Button>
                </div>
              </div>
            </div>
          )}

          {showPicker && (
            <AssignClientPicker
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              onCreateClick={() => setIsCreatingClient(true)}
              onSelectClient={onAssignClient}
              selectedContactId={assignedClient?.id}
            />
          )}
        </div>
      )}
    </Collapsible>
  );
}
